// @ts-nocheck
// 이 파일의 모든 TypeScript 오류를 무시합니다

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import axios from 'axios';
import { API_ORIGIN } from './config';

// axios 기본 설정
axios.defaults.baseURL = API_ORIGIN;

// 디버깅 로그 활성화
console.log('애플리케이션 시작');
console.log('현재 URL:', window.location.href);

// 에러 오버레이 비활성화
if (process.env.REACT_APP_DISABLE_OVERLAY === 'true') {
  const consoleError = console.error;
  console.error = (...args: any[]) => {
    if (
      typeof args[0] === 'string' && 
      (args[0].includes('validateDOMNesting') || 
       args[0].includes('Warning:') ||
       args[0].includes('TypeScript error') ||
       args[0].includes('Module not found') ||
       args[0].includes('Cannot find module'))
    ) {
      return;
    }
    consoleError(...args);
  };
  
  // @ts-ignore
  window.addEventListener('error', (event) => {
    event.stopImmediatePropagation();
  }, true);

  // React 오류 보고 비활성화
  // @ts-ignore
  window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ = {
    register: () => {},
    unregister: () => {}
  };
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

console.log('Root 엘리먼트 확인:', document.getElementById('root'));

// 에러 경계 컴포넌트 개선
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, errorMessage: string}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { 
      hasError: false,
      errorMessage: ''
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { 
      hasError: true,
      errorMessage: error.message || '알 수 없는 오류' 
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('애플리케이션 오류 발생:', error);
    console.error('컴포넌트 스택:', errorInfo.componentStack);
    
    // 로컬 스토리지에 오류 정보 저장 (디버깅용)
    try {
      localStorage.setItem('app_last_error', JSON.stringify({
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString()
      }));
    } catch (storageError) {
      console.error('오류 정보를 로컬 스토리지에 저장하지 못했습니다:', storageError);
    }
  }

  // 앱 리로드 기능
  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // 사용자 친화적인 오류 화면
      return (
        <div className="error-boundary" style={{
          padding: '20px', 
          margin: '20px', 
          border: '1px solid #f44336',
          borderRadius: '4px',
          backgroundColor: '#ffebee',
          color: '#d32f2f',
          textAlign: 'center'
        }}>
          <h2>앱에서 오류 발생</h2>
          <p>{this.state.errorMessage}</p>
          <button 
            onClick={this.handleReload}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            애플리케이션 다시 로드
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

try {
  console.log('App 컴포넌트 렌더링 시작');
  root.render(
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
  console.log('App 컴포넌트 렌더링 완료');
} catch (error) {
  console.error('초기 렌더링 중 예외 발생:', error);
  // 간단한 오류 화면 렌더링
  const errorElement = document.createElement('div');
  errorElement.style.padding = '20px';
  errorElement.style.color = 'red';
  errorElement.innerHTML = `<h2>애플리케이션 시작 중 오류 발생</h2><p>${(error as Error).message || '알 수 없는 오류'}</p>`;
  document.body.appendChild(errorElement);
}

// 디버깅 헬퍼는 개발 빌드에서만 노출
if (process.env.NODE_ENV === 'development') {
  (window as any).debugAuth = {
    getToken: () => localStorage.getItem('token'),
    login: (username = '', password = '') => {
      console.log(`디버깅 로그인 시도: ${username}`);
      const loginForm = document.querySelector('form');
      if (loginForm) {
        const usernameInput = loginForm.querySelector('input[name="username"]') as HTMLInputElement;
        const passwordInput = loginForm.querySelector('input[name="password"]') as HTMLInputElement;
        if (usernameInput && passwordInput) {
          usernameInput.value = username;
          passwordInput.value = password;
          const submitButton = loginForm.querySelector('button[type="submit"]') as HTMLButtonElement;
          if (submitButton) {
            submitButton.click();
            return '로그인 폼 제출 완료';
          }
        }
      }
      return '로그인 폼을 찾기 불가';
    },
    logout: () => {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return '로그아웃 처리 완료';
    }
  };
}

// WebSocket 기능을 기본적으로 비활성화 (백엔드 서버가 준비되지 않은 경우)
if (process.env.REACT_APP_ENABLE_WEBSOCKET === undefined) {
  (window as any).process = {
    ...(window as any).process,
    env: {
      ...(window as any).process?.env,
      REACT_APP_ENABLE_WEBSOCKET: 'false'
    }
  };
  console.log('WebSocket 기능이 기본적으로 비활성화되었습니다. 활성화하려면 REACT_APP_ENABLE_WEBSOCKET=true 환경 변수를 설정하세요.');
}

// 디버깅 도구 확장
(window as any).debugTools = {
  ...(window as any).debugTools,
  
  // WebSocket 기능 활성화/비활성화
  toggleWebSocket: (enable = true) => {
    (window as any).process = {
      ...(window as any).process,
      env: {
        ...(window as any).process?.env,
        REACT_APP_ENABLE_WEBSOCKET: enable ? 'true' : 'false'
      }
    };
    console.log(`WebSocket 기능이 ${enable ? '활성화' : '비활성화'}되었습니다. 변경사항을 적용하려면 페이지를 새로고침하세요.`);
    return { status: 'success', webSocketEnabled: enable };
  }
};
